/**
 * Notification System
 * Displays elegant notifications instead of browser alerts
 */

class NotificationManager {
  constructor() {
    this.container = null;
    this.notifications = [];
    this.init();
  }

  init() {
    // Create notification container if it doesn't exist
    if (!document.querySelector('.notification-container')) {
      this.container = document.createElement('div');
      this.container.className = 'notification-container';
      document.body.appendChild(this.container);
    } else {
      this.container = document.querySelector('.notification-container');
    }
  }

  /**
   * Show a notification
   * @param {string} type - Type of notification: 'error', 'success', 'warning', 'info'
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {number} duration - Duration in ms (default: 5000, 0 = manual close only)
   */
  show(type = 'info', title = '', message = '', duration = 5000) {
    const notification = this.createNotification(type, title, message);

    // Add to container
    this.container.appendChild(notification);
    this.notifications.push(notification);

    // Trigger animation
    requestAnimationFrame(() => {
      notification.classList.add('notification--show');
    });

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.hide(notification);
      }, duration);
    }

    return notification;
  }

  createNotification(type, title, message) {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;

    const icon = this.getIcon(type);

    notification.innerHTML = `
      <div class="notification__icon">
        ${icon}
      </div>
      <div class="notification__content">
        ${title ? `<p class="notification__title">${title}</p>` : ''}
        ${message ? `<p class="notification__message">${message}</p>` : ''}
      </div>
      <button class="notification__close" aria-label="Close notification">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    // Close button handler
    const closeBtn = notification.querySelector('.notification__close');
    closeBtn.addEventListener('click', () => {
      this.hide(notification);
    });

    return notification;
  }

  getIcon(type) {
    const icons = {
      error: `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="16" r="1" fill="currentColor"/>
        </svg>
      `,
      success: `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
      warning: `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 20H22L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 9V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="17" r="1" fill="currentColor"/>
        </svg>
      `,
      info: `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 16V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="8" r="1" fill="currentColor"/>
        </svg>
      `
    };

    return icons[type] || icons.info;
  }

  hide(notification) {
    notification.classList.remove('notification--show');
    notification.classList.add('notification--hide');

    // Remove from DOM after animation
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }

      // Remove from array
      const index = this.notifications.indexOf(notification);
      if (index > -1) {
        this.notifications.splice(index, 1);
      }
    }, 300);
  }

  // Helper methods
  error(title, message, duration = 5000) {
    return this.show('error', title, message, duration);
  }

  success(title, message, duration = 5000) {
    return this.show('success', title, message, duration);
  }

  warning(title, message, duration = 5000) {
    return this.show('warning', title, message, duration);
  }

  info(title, message, duration = 5000) {
    return this.show('info', title, message, duration);
  }

  clearAll() {
    this.notifications.forEach(notification => {
      this.hide(notification);
    });
  }
}

// Initialize global notification manager
if (typeof window !== 'undefined') {
  window.notificationManager = new NotificationManager();
}
